/**
 * Constants and helpers shared between the pre-bootstrap inline tracking
 * script (tracking.inline.ts), the Tracking service, cookieOptionsFactory
 * and the environment configs.
 *
 * The inline script is compiled standalone (scripts/generate-inline-scripts)
 * and injected into the index.html files, so it runs before the app — and
 * before the Angular environment — exists. Everything in this module must
 * therefore stay dependency-free (no Angular, no RxJS), and the environment
 * configs reference these constants so the inline script can never drift
 * from the app config.
 */

export const TRACKING_ENDPOINT = 'https://t.freelancer.com/events';
export const LINK_SESSIONS_ENDPOINT = 'https://t.freelancer.com/link';
export const TRACKING_SESSION_COOKIE = '_tracking_session';

/**
 * localStorage key of the beaconId -> event payload map holding events that
 * haven't been confirmed as sent yet, so they survive hard navigations.
 */
export const TRACKING_BACKLOG_KEY = 'trackingBacklog';

/**
 * Backlog events older than this are dropped instead of being resent: they
 * have repeatedly failed to send and are too old to still be useful.
 */
export const BACKLOG_MAX_AGE_MS = 48 * 60 * 60 * 1000;

export interface TrackingBacklog<T extends object = object> {
  [beaconId: string]: T;
}

export interface InlinePageViewState {
  beaconId: string;
  location: string;
  /**
   * Set when the inline script already delivered the buffered page_view
   * (on pagehide), so the app must not track it again.
   */
  sent?: boolean;
}

export interface InlineTrackingWindow extends Window {
  __flnInlinePageView?: InlinePageViewState;
}

export const generateTrackingUuid = (): string => {
  const s4 = (): string =>
    Math.floor((1 + Math.random()) * 0x1_00_00)
      .toString(16)
      .substring(1);
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
};

/**
 * This domain needs to stay in sync with the `cookieDomain` in
 * `generate-capacitor-config.ts`.
 */
export const getCookieDomain = (hostname: string): string => {
  const match = hostname.match(/freelancer.*$/);
  return match ? `.${match[0]}` : hostname;
};

export const readTrackingBacklog = <
  T extends object = object,
>(): TrackingBacklog<T> =>
  JSON.parse(window.localStorage.getItem(TRACKING_BACKLOG_KEY) || '{}');

export const writeTrackingBacklog = (backlog: TrackingBacklog): void =>
  window.localStorage.setItem(TRACKING_BACKLOG_KEY, JSON.stringify(backlog));

export interface WebappAppVersion {
  gitRevision: string;
  buildTimestampInSeconds: number;
}

/**
 * App version from the `window.webapp` global set in the document <head>.
 * Keep in sync with the web branch of `Pwa.appVersion()` (which can't import
 * this module without creating a tracking <-> pwa package cycle).
 */
export const getWebappAppVersion = (): WebappAppVersion | undefined =>
  window.webapp
    ? {
        gitRevision: window.webapp.version.gitRevision.substring(0, 7),
        buildTimestampInSeconds: Math.round(
          window.webapp.version.buildTimestamp / 1000,
        ),
      }
    : undefined;

export interface TrackingEventBase<
  E extends string,
  P extends string,
  V extends WebappAppVersion,
  D extends object,
> {
  en: E;
  acct: string;
  platform: P;
  isNative: boolean;
  isInstalled: boolean;
  appVersion?: V;
  nativeDeviceInfo?: D;
  t: string;
  location: string;
  user_id?: number;
  session_id: string;
}

/**
 * Base fields shared by every tracking event — the single source of truth
 * for the payload shape. Used by Tracking.track() with live app state, and
 * by the inline script with its pre-bootstrap fallback values. Generic so
 * the service's narrower types (TrackingEventType, Platform, AppVersion,
 * NativeDeviceInfo) flow through unchanged.
 */
export const buildTrackingEventBase = <
  E extends string,
  P extends string,
  V extends WebappAppVersion = WebappAppVersion,
  D extends object = object,
>(
  en: E,
  context: {
    platform: P;
    isNative: boolean;
    isInstalled: boolean;
    appVersion?: V;
    nativeDeviceInfo?: D;
    sessionId: string;
    userId?: number;
  },
): TrackingEventBase<E, P, V, D> => ({
  en,
  acct: window.location.hostname.replace(/^www/, ''),
  platform: context.platform,
  isNative: context.isNative,
  isInstalled: context.isInstalled,
  appVersion: context.appVersion,
  nativeDeviceInfo: context.nativeDeviceInfo,
  t: Date.now().toString(),
  location: window.location.href,
  user_id: context.userId,
  session_id: context.sessionId,
});

export interface PageViewEventData {
  language?: string;
  referrer_url: string;
  screenHeight: number;
  screenWidth: number;
  windowInnerHeight: number;
  windowInnerWidth: number;
  is_404?: boolean;
}

/**
 * The page_view-specific event fields, shared by Tracking.trackPageView()
 * and the inline script so the two events can't drift apart.
 */
export const buildPageViewEventData = (context: {
  language?: string;
  referrerUrl: string;
  is404?: boolean;
}): PageViewEventData => ({
  language: context.language,
  referrer_url: context.referrerUrl,
  screenHeight: window.screen.height,
  screenWidth: window.screen.width,
  windowInnerHeight: window.innerHeight,
  windowInnerWidth: window.innerWidth,
  is_404: context.is404,
});
